import { Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { Observable } from 'rxjs';
import { switchMap, filter, map, take } from 'rxjs/operators';

import { BulkActionFormComponent } from '../MonitoringBulkActionDialog/bulk-action-form.component';
import { Folder } from '../../models/folder.model';
import { FolderService } from '../../../Core/services/folder.service';
import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { ResourceActionTypes } from '../../../../Common/Basic/enums/resource-actions.enum';
import { ChipModel } from '../../../../Common/Forms/models/chip.model';


export class FolderBulkActionParameters{
    showAutoCreateOption: boolean;
    multiSelection?: boolean;
}

@Component({
    selector: 'agpr-bulk-action-form-folder',
    templateUrl: './bulk-action-form-folder.component.html',
    styleUrls: ['./bulk-action-form-folder.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BulkActionFormFolderComponent),
            multi: true
        }],
    standalone: false
})
export class BulkActionFormFolderComponent extends BulkActionFormComponent implements ControlValueAccessor {

    selectedFolder: Folder;
    selectedFolders: Folder[] = [];
    chips: ChipModel[] = [];
    customOptions: AutocompleteCustomOption[] = [];
    folderList$: Observable<Folder[]>;

    @Input() tooltip: string;
    @Input() heading: string = 'Mention.details.folders';
    @Input() multiSelection: boolean = false;
    @Input() set parameters(theParameters: FolderBulkActionParameters) {
        if (theParameters.showAutoCreateOption) {
            this.customOptions = [
                {
                    label: 'Mention.editDialog.folder.createNewFolder',
                    action: (folderName: string) => {
                        const folder = new Folder();
                        folder.name = folderName;

                        this.folderService.create(folder).pipe(
                            switchMap(id => this.folderList$.pipe(
                                map(folders => folders.find(x => x.id === id))
                            )),
                            take(1)
                        ).subscribe(newFolder => {
                            if (this.multiSelection) {
                                this.selectedFolders = [newFolder];
                            } else {
                                this.selectedFolder = newFolder;
                            }
                            this.folderSelected(newFolder);
                        });
                    },
                    appearCondition: AppearCondition.noExactMatchInSuggestions,
                    clearOnSelect: true
                }
            ];
        } else {
            this.customOptions = [];
        }

        this.multiSelection = !!theParameters.multiSelection;
    };

    constructor(public folderService: FolderService) {
        super();
        this.folderList$ = this.folderService.all$;
    }

    writeValue(value: Folder | Folder[]): void {
        if (this.multiSelection) {
            this.selectedFolders = value as Folder[];
            this.updateChips();
        } else {
            this.selectedFolder = value as Folder;
        }
    }

    folderSelected(folder: Folder): void {
        if (this.multiSelection) {
            this.updateChips();
            this.onModelChange(this.selectedFolders);
            return;
        } else {
            this.onModelChange(this.selectedFolder);
        }
    }

    removeChip(index: number): void {
        const chip = this.chips[index];
        this.selectedFolders = this.selectedFolders.filter(folder => folder.id !== chip.id);
        this.updateChips();
        this.onModelChange(this.selectedFolders);
    }

    updateChips(): void {
        this.chips = this.selectedFolders.map(folder => {
            return {
                id: folder.id,
                label: folder.name,
            };
        });
    }
}
